import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

export default function PaymentReturnPage() {
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [status, setStatus] = useState('processing'); // processing, success, failure

  useEffect(() => {
    // Check if opened from popup window
    const isPopup = window.opener !== null;
    
    // Determine payment status from URL params
    // MCB might send: result, resultIndicator, successIndicator, or error
    const result = searchParams.get('result') || 
                   searchParams.get('resultIndicator') || 
                   searchParams.get('status') || 
                   '';
    const error = searchParams.get('error') || searchParams.get('error.explanation') || '';
    const paymentStatus = (result + ' ' + error).toLowerCase();
    
    // Determine status based on params
    let determinedStatus = 'processing';
    if (paymentStatus.includes('success') || 
        paymentStatus.includes('paid') || 
        paymentStatus.includes('completed') ||
        paymentStatus.includes('captured')) {
      determinedStatus = 'success';
    } else if (paymentStatus.includes('fail') || 
               paymentStatus.includes('error') || 
               paymentStatus.includes('declined') ||
               paymentStatus.includes('invalid_request')) {
      determinedStatus = 'failure';
    }
    
    setStatus(determinedStatus);

    // Send message to parent window immediately if opened from popup
    if (isPopup) {
      window.opener.postMessage(
        {
          type: 'PAYMENT_COMPLETE',
          status: determinedStatus,
          result: result,
          timestamp: new Date().toISOString(),
        },
        window.location.origin
      );
    }

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Close popup and redirect parent to orders page
          if (isPopup) {
            try {
              // Redirect parent window to orders page
              if (window.opener) {
                window.opener.location.href = '/user-portal';
              }
              // Close popup window
              window.close();
            } catch (e) {
              // If window can't be closed, at least redirect parent
              if (window.opener) {
                window.opener.location.href = '/user-portal';
              }
            }
          } else {
            // If not popup, redirect to user portal
            window.location.href = '/user-portal';
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [searchParams]);

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return 'Payment Successful!';
      case 'failure':
        return 'Payment Failed';
      default:
        return 'Processing Payment...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#4caf50';
      case 'failure':
        return '#f44336';
      default:
        return '#2196f3';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 3,
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 2,
          padding: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
        }}
      >
        <CircularProgress
          sx={{
            color: getStatusColor(),
            mb: 2,
          }}
        />
        
        <Typography
          variant="h6"
          sx={{
            color: getStatusColor(),
            fontWeight: 600,
            mb: 2,
          }}
        >
          {getStatusMessage()}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#666',
            mb: 3,
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          Redirecting back to website in {countdown} seconds...
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#999',
            fontSize: '0.75rem',
          }}
        >
          This window will close automatically
        </Typography>
      </Box>
    </Box>
  );
}

